import { Star, Upload } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import { useArmLabels } from '../../hooks/use-arm-labels';
import { useExperimentArmComparison } from '../../hooks/use-experiments';
import type { ArmComparisonRow, ArmSlot } from '../../types/experiment.types';
import { armIdToSlot } from '../../utils/arm-labels';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import { ExperimentLiftBadge } from './experiment-lift-badge';
import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  experimentId: string;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

/** Metric-label column width, expressed as `fr` against 1fr per arm column. */
const METRIC_COLUMN_FR = 1.5;

/** Rows rendered while the query is in flight — matches the real row count. */
const SKELETON_ROW_COUNT = 5;

/**
 * Column widths for `table-fixed`, from the 1.5fr : 1fr : 1fr (: 1fr) ratio.
 * Computed rather than hardcoded so the arm columns stay equal whether the
 * experiment runs two arms or three.
 */
function columnWidths(armColumnCount: number): { metric: string; arm: string } {
  const totalFr = METRIC_COLUMN_FR + armColumnCount;
  return {
    metric: `${((METRIC_COLUMN_FR / totalFr) * 100).toFixed(4)}%`,
    arm: `${((1 / totalFr) * 100).toFixed(4)}%`,
  };
}

export function ExperimentArmComparison({ experimentId, segment }: Props) {
  const { t, i18n } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentArmComparison(experimentId, segment);
  // Wire test slots map onto domain arm letters (testA → Arm B, testB → Arm C);
  // `hasTestB` is the ground truth for arm count — analytics data can lag or be
  // null even when a 3-arm experiment is running.
  const { label: armLabel, subtitle: armSubtitle, hasTestB } = useArmLabels(experimentId);

  const armSlots = useMemo<ArmSlot[]>(
    () => (hasTestB ? ['control', 'testA', 'testB'] : ['control', 'testA']),
    [hasTestB],
  );
  const widths = columnWidths(armSlots.length);

  const winnerSlot = !isError && data?.winner ? armIdToSlot(data.winner) : null;

  // Footnote sentence 2, derived from the data rather than asserted: names the
  // metrics the winning arm actually leads. The old copy claimed the winner led
  // "across all key revenue and engagement metrics", which was routinely false —
  // a winner can top the overall tally while trailing on individual rows.
  const winnerSentence = useMemo(() => {
    if (winnerSlot === null || !data) return null;
    const winnerLabel = armLabel(winnerSlot);
    const led = data.rows.filter((row) => row.bestArm === winnerSlot).map((row) => row.metric);
    if (led.length === 0) {
      return t('detail.armComparison.footerWinnerNoLead', { winner: winnerLabel });
    }
    return t('detail.armComparison.footerWinner', {
      winner: winnerLabel,
      metrics: formatList(led, i18n.language),
    });
  }, [winnerSlot, data, armLabel, t, i18n.language]);

  // Credit a non-winning arm that still leads rows, so the footnote never reads
  // as though the winner swept the table.
  const runnerUpSentence = useMemo(() => {
    if (!data || winnerSlot === null) return null;
    const other = armSlots.find(
      (slot) => slot !== winnerSlot && data.rows.some((row) => row.bestArm === slot),
    );
    if (!other) return null;
    const led = data.rows.filter((row) => row.bestArm === other).map((row) => row.metric);
    return t('detail.armComparison.footerLeaders', {
      arm: armLabel(other),
      metrics: formatList(led, i18n.language),
    });
  }, [data, winnerSlot, armSlots, armLabel, t, i18n.language]);

  // Exports the rendered rows — values are already formatted strings from the API.
  const handleExport = () => {
    if (!data || data.rows.length === 0) return;
    const liftLabel = t('detail.armComparison.liftLabel');
    const csvRows = data.rows.map((row) => ({
      metric: row.metric,
      control: row.control,
      testA: row.testA,
      testALift: row.testALift,
      testB: row.testB,
      testBLift: row.testBLift,
    }));
    downloadCsv('arm-comparison', csvRows, [
      { key: 'metric', label: t('detail.armComparison.metric') },
      { key: 'control', label: armLabel('control') },
      { key: 'testA', label: armLabel('testA') },
      { key: 'testALift', label: `${armLabel('testA')} ${liftLabel}` },
      ...(hasTestB
        ? [
            { key: 'testB' as const, label: armLabel('testB') },
            { key: 'testBLift' as const, label: `${armLabel('testB')} ${liftLabel}` },
          ]
        : []),
    ]);
  };

  const isExportDisabled = isLoading || isError || !data || data.rows.length === 0;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[16px] font-medium">{t('detail.armComparison.title')}</h3>
            <p className="text-muted-foreground text-sm">{t('detail.armComparison.subtitle')}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExportDisabled}
            className={cn('gap-2', isExportDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('detail.armComparison.exportCsv')}
          </Button>
        </div>

        {/* Table — collapses to a "data unavailable" line when the source
            query throws (no backend endpoint yet), so the card never renders
            a fabricated Winner / Arm B / Arm C row.

            `overflow-x-auto` + the table's min-width lets narrow viewports
            scroll instead of crushing the arm columns. */}
        {isError ? (
          <ExperimentDataUnavailable />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed text-sm">
              <caption className="sr-only">{t('detail.armComparison.caption')}</caption>
              <colgroup>
                <col style={{ width: widths.metric }} />
                {armSlots.map((slot) => (
                  <col key={slot} style={{ width: widths.arm }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="pb-4 text-left font-normal">
                    <span className="sr-only">{t('detail.armComparison.metric')}</span>
                  </th>
                  {armSlots.map((slot) => (
                    <ArmHeaderCell
                      key={slot}
                      label={armLabel(slot)}
                      subtitle={armSubtitle(slot)}
                      isWinner={winnerSlot === slot}
                      winnerLabel={t('detail.armComparison.winner')}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading || !data
                  ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="border-t">
                        <td className="py-4 pr-4">
                          <Skeleton className="h-4 w-32" />
                        </td>
                        {armSlots.map((slot) => (
                          <td key={slot} className="px-4 py-4 align-top">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="mt-2 h-4 w-14" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : data.rows.map((row, rowIndex) => (
                      <tr key={row.metric} className="border-t">
                        <th
                          scope="row"
                          className="text-foreground py-4 pr-4 text-left align-top font-medium"
                        >
                          <span className="flex flex-wrap items-center gap-1.5">
                            {row.metric}
                            <RevenueSourceTag source={row.source} />
                          </span>
                        </th>
                        {armSlots.map((slot) => (
                          <ArmValueCell
                            key={slot}
                            value={valueFor(row, slot)}
                            lift={liftFor(row, slot)}
                            isBest={row.bestArm === slot}
                            isWinner={winnerSlot === slot}
                            isLastRow={rowIndex === data.rows.length - 1}
                            baselineLabel={t('detail.armComparison.baseline')}
                            bestLabel={t('detail.armComparison.bestLegend')}
                          />
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend for the accent dot */}
        {!isError && (
          <p className="text-muted-foreground flex items-center gap-2 text-xs">
            <span className="bg-primary h-1.5 w-1.5 shrink-0 rounded-full" aria-hidden="true" />
            {t('detail.armComparison.bestLegend')}
          </p>
        )}

        {/* Footer — the winner sentences are appended only once a winner is
            known (omitted while loading / on error) and name the metrics each
            arm actually leads. */}
        <p className="text-muted-foreground text-sm">
          {t('detail.armComparison.footer')}
          {winnerSentence && ` ${winnerSentence}`}
          {runnerUpSentence && ` ${runnerUpSentence}`}
        </p>
      </CardContent>
    </Card>
  );
}

/** Pre-formatted display value for one arm slot. */
function valueFor(row: ArmComparisonRow, slot: ArmSlot): string {
  if (slot === 'testA') return row.testA;
  if (slot === 'testB') return row.testB;
  return row.control;
}

/** Pre-formatted lift string, or `undefined` for Control (the baseline has no lift). */
function liftFor(row: ArmComparisonRow, slot: ArmSlot): string | undefined {
  if (slot === 'testA') return row.testALift;
  if (slot === 'testB') return row.testBLift;
  return undefined;
}

/** Joins metric names into a natural-language list ("A, B and C"). */
function formatList(items: string[], locale: string): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
}

interface ArmHeaderCellProps {
  label: string;
  subtitle: string;
  isWinner: boolean;
  winnerLabel: string;
}

/**
 * Arm column header. The Winner badge sits on its own line directly above the
 * arm name so it reads as a label on that column rather than floating to the
 * right edge. The winning arm also carries the top of the highlight band.
 */
function ArmHeaderCell({ label, subtitle, isWinner, winnerLabel }: ArmHeaderCellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 pt-4 pb-4 text-left align-bottom font-normal',
        isWinner && 'bg-primary/5 rounded-t-md',
      )}
    >
      {isWinner && (
        <span className="bg-primary text-primary-foreground mb-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase">
          <Star className="h-3 w-3 fill-current" aria-hidden="true" />
          {winnerLabel}
        </span>
      )}
      <p className="text-foreground font-semibold">{label}</p>
      <p className="text-muted-foreground text-xs font-normal">{subtitle}</p>
    </th>
  );
}

interface ArmValueCellProps {
  value: string;
  /** Omitted for Control, which shows the baseline caption instead. */
  lift?: string | undefined;
  isBest: boolean;
  /** Winning arm — carries the highlight band, so it matches the header badge. */
  isWinner: boolean;
  isLastRow: boolean;
  baselineLabel: string;
  bestLabel: string;
}

/**
 * One arm's value for one metric: value on the first line, lift badge (or the
 * Control baseline caption) on the second. The accent dot sits in a fixed-width
 * gutter that is reserved whether or not the dot renders, so values across a row
 * share one indent instead of shifting by the dot's width.
 *
 * The highlight band tracks the winning arm — the same column the header badge
 * marks — rather than being pinned to Control.
 */
function ArmValueCell({
  value,
  lift,
  isBest,
  isWinner,
  isLastRow,
  baselineLabel,
  bestLabel,
}: ArmValueCellProps) {
  return (
    <td
      className={cn(
        'px-4 py-4 align-top',
        isWinner && 'bg-primary/5',
        isWinner && isLastRow && 'rounded-b-md',
      )}
    >
      <div className="flex items-start gap-1.5">
        <span
          className={cn(
            'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
            isBest ? 'bg-primary' : 'bg-transparent',
          )}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-foreground font-semibold tabular-nums">
            {isBest && <span className="sr-only">{bestLabel}. </span>}
            {value}
          </p>
          {lift === undefined ? (
            <p className="text-muted-foreground mt-1 text-xs">{baselineLabel}</p>
          ) : (
            <ExperimentLiftBadge value={lift} className="mt-1 tabular-nums" />
          )}
        </div>
      </div>
    </td>
  );
}
